/**
 * One gate in front of every renderer-initiated token refresh.
 *
 * - Single-flight: N concurrent 401s (the notification poll, a profile
 *   fetch, a library write landing together) share ONE refresh round trip
 *   instead of racing N of them. Each of those counted against the auth
 *   service's per-IP refresh limit.
 * - Cooldown: a 429 from the refresh endpoint parks further attempts for the
 *   Retry-After it named (main passes it through as `retryAfterMs`). Callers
 *   get the same 429-shaped response back instantly, so their request fails
 *   fast instead of feeding the lockout — a 401 → refresh → 429 loop across
 *   the pollers is what kept the 2026-09-02 lockout alive for an hour.
 *
 * Adoption of a fresh token (localStorage / Redux) stays with the callers:
 * refreshAndRetry and the auth thunk each do it their own way on purpose.
 */
const DEFAULT_COOLDOWN_MS = 60 * 1000;
const MAX_COOLDOWN_MS = 15 * 60 * 1000;

let inflight = null;
let inflightKey = null;
let blockedUntil = 0;

export function refreshCooldownMs(now = Date.now()) {
    return Math.max(0, blockedUntil - now);
}

function rateLimitedResponse(remainingMs) {
    return { error: 'Too many refresh attempts. Please try again later.', status: 429, retryAfterMs: remainingMs };
}

/**
 * Ask main (or the web shim) for a fresh access token. Resolves to the raw
 * refresh response: `{ accessToken, ... }` on success, `{ error, status }`
 * on failure. Never throws.
 */
export function requestRefresh(refreshToken) {
    const remaining = refreshCooldownMs();
    if (remaining > 0) return Promise.resolve(rateLimitedResponse(remaining));

    const key = refreshToken || 'cookie';
    if (inflight && inflightKey === key) return inflight;

    inflightKey = key;
    inflight = (async () => {
        try {
            const raw = await window.electron.auth.refreshToken(refreshToken);
            const response = raw && typeof raw === 'object' ? raw : { error: 'Token refresh failed' };
            if (response.accessToken) {
                blockedUntil = 0;
            } else if (response.status === 429) {
                const wanted = Number(response.retryAfterMs);
                const ms = Number.isFinite(wanted) && wanted > 0 ? wanted : DEFAULT_COOLDOWN_MS;
                blockedUntil = Date.now() + Math.min(MAX_COOLDOWN_MS, ms);
            }
            return response;
        } catch (err) {
            return { error: err?.message || 'Token refresh failed' };
        } finally {
            inflight = null;
            inflightKey = null;
        }
    })();
    return inflight;
}

/** Tests and sign-out: forget the in-flight call and the cooldown. */
export function resetRefreshGate() {
    inflight = null;
    inflightKey = null;
    blockedUntil = 0;
}
